'use client'

import { useEffect, useRef, useState, type FormEvent } from 'react'
import Link from 'next/link'
import Image from 'next/image'
import {
  ECHEANCES, FAMILLES_VOULUES, OBJECTIFS, SUJET_QUESTIONNAIRE, VOLUMES,
  lireCodesPostaux, messageQuestionnaire,
  type Choix, type Echeance, type FamilleVoulue, type Locale, type Objectif, type ReponsesQuestionnaire, type Volume,
} from '@/lib/site/accueil'
import type { ProjetQuestionnaireReponse } from '@/app/api/questionnaire-projets/route'
import { textesAccueil } from './accueil-textes'
import { signalerDemandeEntreprise } from '@/lib/acquisition/evenements'
import { origineAJoindre } from '@/lib/acquisition/origine'

/**
 * Questionnaire d'orientation de l'accueil (ancre #questionnaire).
 *
 * Quatre questions, une par écran, un clic par réponse (la dernière réunit
 * la localisation et l'échéance), barre de progression, retour possible.
 * Résultat : trois projets réels du catalogue (POST /api/questionnaire-projets,
 * règle dans src/lib/site/accueil.ts), puis un formulaire court qui crée la
 * demande dans la collection `contacts` par le traitement du formulaire de
 * contact (/api/formulaire-contact : plafond par adresse, champ piège
 * anti-robot, e-mail d'alerte interne), sujet « Demande de devis
 * (questionnaire) », réponses et projets proposés dans le message.
 *
 * Aucune donnée dans l'adresse (tout part en POST). Une demande enregistrée
 * est une demande d'entreprise : conversion `demande_entreprise` (sans
 * donnée saisie, src/lib/acquisition/evenements.ts) et source publicitaire
 * du clic jointe au corps POST si le visiteur l'a permise
 * (src/lib/acquisition/origine.ts).
 */
type Etape = 1 | 2 | 3 | 4 | 5

interface Brouillon {
  objectif?: Objectif
  volume?: Volume
  famille?: FamilleVoulue
  regions: string[]
  codes: string
  echeance?: Echeance
}

const PASTILLE = 'text-left rounded-2xl border px-4 py-3.5 transition-colors duration-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-forest'
const pastille = (actif: boolean) =>
  `${PASTILLE} ${actif ? 'border-2 border-forest bg-sage text-forest font-semibold' : 'border-[#CFD6D1] bg-white text-[#3B4A42] hover:border-forest'}`
const CHAMP = 'w-full rounded-xl border border-[#CFD6D1] bg-white px-4 py-3 text-[15px] text-[#14281F] focus:outline-none focus:border-forest focus:ring-2 focus:ring-forest/20'

export default function Questionnaire({ locale, regions }: { locale: Locale; regions: { valeur: string; nom: string }[] }) {
  const t = textesAccueil(locale).questionnaire
  const en = locale === 'en'
  const lib = <T extends string>(c: Choix<T>) => (en ? c.en : c.fr)
  const detail = <T extends string>(c: Choix<T>) => (en ? c.detailEn : c.detailFr)

  const [etape, setEtape] = useState<Etape>(1)
  const [b, setB] = useState<Brouillon>({ regions: [], codes: '' })
  const [projets, setProjets] = useState<ProjetQuestionnaireReponse[] | null>(null)
  const [chargement, setChargement] = useState(false)
  const [erreurProjets, setErreurProjets] = useState(false)
  const [envoi, setEnvoi] = useState<'repos' | 'en-cours' | 'fait' | 'erreur'>('repos')
  const titreRef = useRef<HTMLHeadingElement>(null)
  const premierRendu = useRef(true)

  // À chaque écran, le focus va à la question (lecteurs d'écran, clavier), sans faire défiler la page.
  useEffect(() => {
    if (premierRendu.current) { premierRendu.current = false; return }
    titreRef.current?.focus({ preventScroll: true })
  }, [etape])

  const reponses = (): ReponsesQuestionnaire | null =>
    b.objectif && b.volume && b.famille && b.echeance
      ? { objectif: b.objectif, volume: b.volume, famille: b.famille, regions: b.regions, codesPostaux: lireCodesPostaux(b.codes), echeance: b.echeance }
      : null

  async function chercherProjets() {
    const r = reponses()
    if (!r) return
    setEtape(5)
    setChargement(true)
    setErreurProjets(false)
    try {
      const res = await fetch('/api/questionnaire-projets', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(r),
      })
      if (!res.ok) throw new Error(String(res.status))
      setProjets(((await res.json()) as { projets: ProjetQuestionnaireReponse[] }).projets)
    } catch {
      setProjets(null)
      setErreurProjets(true)
    } finally {
      setChargement(false)
    }
  }

  async function envoyer(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const r = reponses()
    if (!r) return
    const f = e.currentTarget
    const val = (nom: string) => ((f.elements.namedItem(nom) as HTMLInputElement | null)?.value ?? '').trim()
    setEnvoi('en-cours')
    try {
      const res = await fetch('/api/formulaire-contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: val('name'),
          email: val('email'),
          company: val('company'),
          phone: val('phone') || undefined,
          subject: SUJET_QUESTIONNAIRE,
          message: messageQuestionnaire(r, (projets ?? []).map((p) => ({ titre: p.titre, slug: p.slug, methode: p.methodeFr, region: p.region, tco2: p.tco2 }))),
          siteWeb: val('site_web'),
          origineAcquisition: origineAJoindre(),
        }),
      })
      if (res.ok) signalerDemandeEntreprise('questionnaire-orientation')
      setEnvoi(res.ok ? 'fait' : 'erreur')
    } catch {
      setEnvoi('erreur')
    }
  }

  const progression = etape >= 5 ? 100 : (etape - 1) * 25
  const choisir = <K extends 'objectif' | 'volume' | 'famille'>(cle: K, v: Brouillon[K], suite: Etape) => {
    setB((x) => ({ ...x, [cle]: v }))
    setEtape(suite)
  }
  const basculerRegion = (r: string) =>
    setB((x) => ({ ...x, regions: x.regions.includes(r) ? x.regions.filter((y) => y !== r) : [...x.regions, r] }))

  const titreQuestion = (texte: string, aide?: string) => (
    <div className="mb-5">
      <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#8A958F] mb-2">{t.etape(etape)}</p>
      <h3 ref={titreRef} tabIndex={-1} className="font-heading font-bold text-xl md:text-[22px] text-[#14281F] outline-none" data-testid="question-titre">{texte}</h3>
      {aide && <p className="text-sm text-[#6B7A72] mt-1.5">{aide}</p>}
    </div>
  )

  const retour = (vers: Etape) => (
    <button type="button" onClick={() => setEtape(vers)} className="text-sm font-semibold text-[#4A5A52] hover:text-forest underline-offset-4 hover:underline px-1 py-2" data-testid="questionnaire-retour">
      ← {t.retour}
    </button>
  )

  return (
    <section id="questionnaire" className="scroll-mt-24 py-20 md:py-28 bg-cream" data-testid="questionnaire">
      <div className="container-site">
        <div className="bg-white border border-[#E6E4DC] rounded-[28px] p-6 sm:p-10 lg:p-14">
          <div className={etape === 5 ? 'flex flex-col gap-10' : 'grid grid-cols-1 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] gap-10 lg:gap-14 items-start'}>
            <div className={etape === 5 ? 'max-w-[760px]' : ''}>
              <p className="text-[13px] font-bold uppercase tracking-[0.12em] text-[#B07F22] mb-3.5">{t.surtitre}</p>
              <h2 className="font-heading font-extrabold text-[28px] md:text-[36px] leading-[1.15] text-[#14281F]">{etape === 5 ? t.resultatTitre : t.titre}</h2>
              <p className="mt-4 text-[#4A5A52] text-base leading-relaxed">{etape === 5 ? t.resultatTexte : t.texte}</p>
            </div>

            <div className="min-w-0">
              <div
                className="h-1.5 bg-[#EEF1EC] rounded-full mb-7 overflow-hidden"
                role="progressbar" aria-label={t.progression} aria-valuemin={0} aria-valuemax={100} aria-valuenow={progression}
              >
                <div className="h-full bg-amber rounded-full transition-[width] duration-500" style={{ width: `${Math.max(progression, 6)}%` }} />
              </div>

              {etape === 1 && (
                <div>
                  {titreQuestion(t.q1)}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                    {OBJECTIFS.map((c) => (
                      <button key={c.valeur} type="button" className={pastille(b.objectif === c.valeur)} aria-pressed={b.objectif === c.valeur} onClick={() => choisir('objectif', c.valeur, 2)} data-testid={`q1-${c.valeur}`}>
                        {lib(c)}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {etape === 2 && (
                <div>
                  {titreQuestion(t.q2, t.q2aide)}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                    {VOLUMES.map((c) => (
                      <button key={c.valeur} type="button" className={pastille(b.volume === c.valeur)} aria-pressed={b.volume === c.valeur} onClick={() => choisir('volume', c.valeur, 3)} data-testid={`q2-${c.valeur}`}>
                        <span className="block">{lib(c)}</span>
                        {detail(c) && <span className="block text-[13px] font-normal text-[#6B7A72] mt-0.5">{detail(c)}</span>}
                      </button>
                    ))}
                  </div>
                  <div className="mt-5">{retour(1)}</div>
                </div>
              )}

              {etape === 3 && (
                <div>
                  {titreQuestion(t.q3)}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                    {FAMILLES_VOULUES.map((c) => (
                      <button key={c.valeur} type="button" className={pastille(b.famille === c.valeur)} aria-pressed={b.famille === c.valeur} onClick={() => choisir('famille', c.valeur, 4)} data-testid={`q3-${c.valeur}`}>
                        <span className="block">{lib(c)}</span>
                        {detail(c) && <span className="block text-[13px] font-normal text-[#6B7A72] mt-0.5">{detail(c)}</span>}
                      </button>
                    ))}
                  </div>
                  <div className="mt-5">{retour(2)}</div>
                </div>
              )}

              {etape === 4 && (
                <div>
                  {titreQuestion(t.q4)}
                  <fieldset>
                    <legend className="text-sm font-semibold text-[#14281F] mb-2.5">{t.q4regions}</legend>
                    <div className="flex flex-wrap gap-2">
                      <button type="button" aria-pressed={b.regions.length === 0} onClick={() => setB((x) => ({ ...x, regions: [] }))} className={`${pastille(b.regions.length === 0)} !rounded-full !py-2 text-sm`} data-testid="q4-partout">
                        {t.q4partout}
                      </button>
                      {regions.map((r) => (
                        <button key={r.valeur} type="button" aria-pressed={b.regions.includes(r.valeur)} onClick={() => basculerRegion(r.valeur)} className={`${pastille(b.regions.includes(r.valeur))} !rounded-full !py-2 text-sm`} data-testid="q4-region">
                          {r.nom}
                        </button>
                      ))}
                    </div>
                  </fieldset>
                  <label className="block mt-6">
                    <span className="block text-sm font-semibold text-[#14281F] mb-1.5">{t.q4cp}</span>
                    <input
                      type="text" inputMode="numeric" autoComplete="off" name="codes-postaux"
                      value={b.codes} onChange={(e) => setB((x) => ({ ...x, codes: e.target.value }))}
                      placeholder="44000, 35200" className={`${CHAMP} max-w-[360px]`} data-testid="q4-codes"
                      aria-describedby="q4-codes-aide"
                    />
                    <span id="q4-codes-aide" className="block text-[13px] text-[#6B7A72] mt-1.5">{t.q4cpAide}</span>
                  </label>
                  <fieldset className="mt-6">
                    <legend className="text-sm font-semibold text-[#14281F] mb-2.5">{t.q4echeance}</legend>
                    <div className="flex flex-wrap gap-2">
                      {ECHEANCES.map((c) => (
                        <button key={c.valeur} type="button" aria-pressed={b.echeance === c.valeur} onClick={() => setB((x) => ({ ...x, echeance: c.valeur }))} className={`${pastille(b.echeance === c.valeur)} !rounded-full !py-2 text-sm`} data-testid={`q4-${c.valeur}`}>
                          {lib(c)}
                        </button>
                      ))}
                    </div>
                  </fieldset>
                  <div className="flex flex-wrap items-center gap-4 mt-8">
                    <button
                      type="button" disabled={!b.echeance} onClick={chercherProjets}
                      className="inline-flex items-center justify-center rounded-full bg-amber text-[#14281F] font-heading font-bold px-7 py-3.5 text-[15px] transition-all duration-300 hover:brightness-95 disabled:opacity-45 disabled:cursor-not-allowed focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-forest"
                      data-testid="voir-projets"
                    >
                      {t.voirProjets}
                    </button>
                    {retour(3)}
                  </div>
                </div>
              )}
            </div>
          </div>

          {etape === 5 && (
            <div className="mt-2" aria-live="polite">
              <h3 ref={titreRef} tabIndex={-1} className="sr-only">{t.resultatTitre}</h3>
              {chargement && <p className="text-[#4A5A52]">{t.chargement}</p>}
              {erreurProjets && (
                <div className="flex flex-wrap items-center gap-4">
                  <p className="text-[#8B2E1F]">{t.erreurProjets}</p>
                  <button type="button" onClick={chercherProjets} className="text-forest font-semibold underline">{t.voirProjets}</button>
                </div>
              )}
              {projets && projets.length === 0 && <p className="text-[#4A5A52]">{t.aucun}</p>}
              {projets && projets.length > 0 && (
                <ul className="grid grid-cols-1 md:grid-cols-3 gap-5" data-testid="projets-proposes">
                  {projets.map((p) => (
                    <li key={p.slug} className="bg-white border border-[#E6E4DC] rounded-2xl overflow-hidden flex flex-col" data-testid="projet-propose">
                      <div className="relative h-40 bg-sage">
                        {p.photo && <Image src={p.photo.url} alt={p.photo.alt} fill sizes="(max-width: 768px) 100vw, 33vw" className="object-cover" />}
                      </div>
                      <div className="p-5 flex flex-col gap-1.5 flex-grow">
                        <span className="text-xs font-bold uppercase tracking-[0.08em] text-forest-light">{en ? p.methodeEn : p.methodeFr}</span>
                        <span className="font-heading font-bold text-[17px] text-[#14281F] leading-snug">{p.titre}</span>
                        <span className="text-sm text-[#6B7A72]">{p.region} · {textesAccueil(locale).carte.tco2(p.tco2)}</span>
                        {/* Distance aux sites (codes postaux) si elle parle ; sinon « dans votre région » */}
                        {p.distanceKm != null && (p.distanceKm <= 150 || !p.dansRegion) ? (
                          <span className="text-sm text-forest font-medium">{t.distance(p.distanceKm)}</span>
                        ) : p.dansRegion ? (
                          <span className="text-sm text-forest font-medium">{t.dansRegion}</span>
                        ) : null}
                        <Link href={en ? `/en/projets/${p.slug}` : `/projets/${p.slug}`} className="mt-auto pt-3 text-forest font-bold text-sm hover:text-forest-light">
                          {t.voirFiche} <span aria-hidden="true">→</span>
                        </Link>
                      </div>
                    </li>
                  ))}
                </ul>
              )}

              {!chargement && (
                <div className="mt-10 grid grid-cols-1 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] gap-8 lg:gap-14 items-start border-t border-[#EEF1EC] pt-10">
                  <div>
                    <h3 className="font-heading font-extrabold text-2xl text-[#14281F]">{t.formTitre}</h3>
                    <p className="mt-3 text-[#4A5A52] leading-relaxed">{t.formTexte}</p>
                    <button type="button" onClick={() => { setEnvoi('repos'); setEtape(1) }} className="mt-4 text-sm font-semibold text-[#4A5A52] hover:text-forest underline underline-offset-4" data-testid="questionnaire-recommencer">
                      {t.recommencer}
                    </button>
                  </div>
                  {envoi === 'fait' ? (
                    <div className="rounded-2xl bg-sage p-7" role="status" data-testid="questionnaire-merci">
                      <p className="font-heading font-bold text-xl text-forest">{t.merciTitre}</p>
                      <p className="mt-2 text-[#3B4A42]">{t.merciTexte}</p>
                    </div>
                  ) : (
                    <form onSubmit={envoyer} className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="questionnaire-formulaire">
                      <label className="block">
                        <span className="block text-sm font-semibold text-[#14281F] mb-1.5">{t.nom}</span>
                        <input name="name" required autoComplete="name" className={CHAMP} />
                      </label>
                      <label className="block">
                        <span className="block text-sm font-semibold text-[#14281F] mb-1.5">{t.email}</span>
                        <input name="email" type="email" required autoComplete="email" className={CHAMP} />
                      </label>
                      <label className="block">
                        <span className="block text-sm font-semibold text-[#14281F] mb-1.5">{t.entreprise}</span>
                        <input name="company" required autoComplete="organization" className={CHAMP} />
                      </label>
                      <label className="block">
                        <span className="block text-sm font-semibold text-[#14281F] mb-1.5">{t.telephone} <span className="font-normal text-[#6B7A72]">({t.facultatif})</span></span>
                        <input name="phone" type="tel" autoComplete="tel" className={CHAMP} />
                      </label>
                      {/* Champ piège : invisible pour un visiteur, rempli par les robots (refusé sans bruit par l'API) */}
                      <div className="absolute -left-[9999px] w-px h-px overflow-hidden" aria-hidden="true">
                        <label>Site web<input name="site_web" type="text" tabIndex={-1} autoComplete="off" defaultValue="" /></label>
                      </div>
                      <label className="sm:col-span-2 flex items-start gap-3 text-sm text-[#4A5A52] leading-relaxed">
                        <input type="checkbox" required className="mt-1 w-4 h-4 accent-forest flex-shrink-0" name="consentement" />
                        <span>
                          {t.consentement}{' '}
                          <Link href={en ? '/en/confidentialite' : '/confidentialite'} className="text-forest underline">{t.confidentialite}</Link>.
                        </span>
                      </label>
                      <div className="sm:col-span-2 flex flex-wrap items-center gap-4">
                        <button
                          type="submit" disabled={envoi === 'en-cours'}
                          className="inline-flex items-center justify-center rounded-full bg-forest text-white font-semibold px-7 py-3.5 text-[15px] transition-colors duration-300 hover:bg-forest-light disabled:opacity-60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-forest"
                          data-testid="questionnaire-envoyer"
                        >
                          {envoi === 'en-cours' ? t.envoi : t.envoyer}
                        </button>
                        {envoi === 'erreur' && <p className="text-sm text-[#8B2E1F]" role="alert">{t.erreurEnvoi}</p>}
                      </div>
                    </form>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  )
}
